import { useState, useCallback, useRef } from 'react';
import SearchPanel from './components/SearchPanel';
import ResultsPanel from './components/ResultsPanel';
import ExportModal from './components/ExportModal';
import SettingsPanel from './components/SettingsPanel';
import './App.css';

const N8N_BASE = process.env.REACT_APP_N8N_URL || 'https://n8n.one.vision/webhook';

export default function App() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showExport, setShowExport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    tedApiKey: '',
    jarvisApiKey: '',
    jarvisModel: 'deepseek v4flash',
    opUrl: 'https://opp.one.vision',
    opApiKey: '',
    opProjectId: '',
    spToken: '',
    aiEnabled: true,
  });

  const search = useCallback(async (params) => {
    setLoading(true);
    setStatus({ type: 'info', text: 'Suche läuft…' });
    setResults([]);
    setSelected(new Set());

    try {
      const res = await fetch(`${N8N_BASE}/ted-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          tedApiKey: settings.tedApiKey,
          jarvisApiKey: settings.jarvisApiKey,
          jarvisModel: settings.jarvisModel,
          aiEnabled: settings.aiEnabled,
        }),
      });

      if (!res.ok) throw new Error(`n8n: ${res.status}`);
      const data = await res.json();
      const notices = data.notices || [];
      setResults(notices);
      setStatus({ type: 'success', text: `${notices.length} Ausschreibungen gefunden${settings.aiEnabled ? ' · KI-Analyse läuft…' : ''}` });
    } catch (e) {
      setStatus({ type: 'error', text: `Fehler: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, [settings]);

  const exportSelected = useCallback(async (exportSettings) => {
    const notices = results.filter(r => selected.has(r.id));
    const errors = [];
    const successes = [];

    for (const notice of notices) {
      try {
        const res = await fetch(`${N8N_BASE}/op-export`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notice,
            opUrl: settings.opUrl,
            opApiKey: settings.opApiKey,
            projectIdentifier: exportSettings.projectId || settings.opProjectId,
            projectName: exportSettings.projectName,
            buero: exportSettings.buero,
            spToken: settings.spToken,
            createFolder: exportSettings.createFolder,
            tasks: exportSettings.tasks,
          }),
        });
        const data = await res.json();
        if (data.success) successes.push({ notice, ...data });
        else errors.push({ notice, error: data.error || 'Unbekannter Fehler' });
      } catch (e) {
        errors.push({ notice, error: e.message });
      }
    }

    return { successes, errors };
  }, [results, selected, settings]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <span className="logo-mark">TED</span>
          <span className="logo-sub">Ausschreibungssuche</span>
        </div>
        <div className="header-right">
          <span className="cpv-badge">CPV 71000000</span>
          <span className="cpv-badge active">CPV 71200000</span>
          <button className="btn-icon" onClick={() => setShowSettings(true)} title="Einstellungen">⚙️</button>
        </div>
      </header>

      <div className="app-body">
        <SearchPanel onSearch={search} loading={loading} />

        <main className="main-content">
          {status && (
            <div className={`status-banner status-${status.type}`}>
              {status.text}
            </div>
          )}

          <ResultsPanel
            results={results}
            loading={loading}
            selected={selected}
            onSelect={setSelected}
            onExport={() => setShowExport(true)}
          />
        </main>
      </div>

      {showExport && (
        <ExportModal
          selected={selected}
          results={results}
          settings={settings}
          onExport={exportSelected}
          onClose={() => setShowExport(false)}
        />
      )}

      {showSettings && (
        <SettingsPanel
          settings={settings}
          onChange={setSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
